// E8CuboidBoxWithCubeVolumeClosedTask.js
// Zadanie zamknięte E8 (2025 CZE zad. 15) - kostka w prostopadłościennym pudełku.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath, ReadableInlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import CuboidBoxDiagram from "../../../../components/diagrams/CuboidBoxDiagram";

const LETTERS = ["A", "B", "C", "D"];

const CASES = [
  {
    cubeVolume: 1,
    relations: {
      a: { num: 1, den: 3 },
      b: { num: 1, den: 2 },
      c: { num: 2, den: 5 },
    },
    fixedOptions: [12, 18, 10, 15],
  },
  {
    cubeVolume: 1,
    relations: {
      a: { num: 1, den: 4 },
      b: { num: 1, den: 3 },
      c: { num: 2, den: 5 },
    },
  },
  {
    cubeVolume: 8,
    relations: {
      a: { num: 1, den: 3 },
      b: { num: 2, den: 5 },
      c: { num: 1, den: 4 },
    },
  },
  {
    cubeVolume: 8,
    relations: {
      a: { num: 2, den: 5 },
      b: { num: 1, den: 3 },
      c: { num: 1, den: 2 },
    },
  },
  {
    cubeVolume: 27,
    relations: {
      a: { num: 1, den: 5 },
      b: { num: 1, den: 3 },
      c: { num: 3, den: 4 },
    },
  },
  {
    cubeVolume: 27,
    relations: {
      a: { num: 3, den: 5 },
      b: { num: 1, den: 4 },
      c: { num: 1, den: 3 },
    },
  },
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function cubeRootInt(n) {
  return Math.round(Math.cbrt(n));
}

function fracLatex({ num, den }) {
  return `\\frac{${num}}{${den}}`;
}

function reciprocalLatex({ num, den }) {
  return num === 1 ? String(den) : `\\frac{${den}}{${num}}`;
}

function edgeLength(cubeEdge, rel) {
  return (cubeEdge * rel.den) / rel.num;
}

function formatNumber(value) {
  return Number.isInteger(value) ? String(value) : String(value).replace(".", ",");
}

function makeOptions(volume, fixedOptions) {
  if (fixedOptions) {
    return {
      options: fixedOptions.map((value) => ({ value })),
      correctIndex: fixedOptions.findIndex((value) => value === volume),
    };
  }

  const candidates = [
    volume,
    Math.round(volume * 0.5),
    Math.round(volume * 1.5),
    Math.round(volume + 3),
    Math.round(volume - 3),
    Math.round(volume * 2),
  ].filter((value) => value > 0);
  const unique = [...new Set(candidates)].slice(0, 4);
  while (unique.length < 4) unique.push(volume + unique.length * 2 + 1);
  const options = shuffle(unique).map((value) => ({ value }));
  return { options, correctIndex: options.findIndex((option) => option.value === volume) };
}

function buildData(config) {
  const cubeEdge = cubeRootInt(config.cubeVolume);
  const a = edgeLength(cubeEdge, config.relations.a);
  const b = edgeLength(cubeEdge, config.relations.b);
  const c = edgeLength(cubeEdge, config.relations.c);
  const volume = a * b * c;
  const optionData = makeOptions(volume, config.fixedOptions);
  return {
    cubeVolume: config.cubeVolume,
    cubeEdge,
    relations: config.relations,
    a,
    b,
    c,
    volume,
    options: optionData.options,
    correctIndex: optionData.correctIndex,
  };
}

function createOriginalTask() {
  return {
    type: "E8CuboidBoxWithCubeVolumeClosedTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 15 },
    tags: ["e8", "stereometria", "prostopadloscian", "szescian", "objetosc"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8CuboidBoxWithCubeVolumeClosedTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_15" },
    tags: ["e8", "stereometria", "prostopadloscian", "szescian", "objetosc"],
  };
}

function Diagram({ data }) {
  return (
    <div style={{ display: "grid", placeItems: "center", margin: "12px 0 24px" }}>
      <div style={{ width: "min(100%, 320px)" }}>
        <CuboidBoxDiagram
        widthValue={data.a}
        depthValue={data.b}
        heightValue={data.c}
        frontLabel="a"
        depthLabel="b"
        heightLabel="c"
        showInnerCube
        innerCubeEdgeValue={data.cubeEdge}
        width={320}
        height={220}
      />
      </div>
    </div>
  );
}

export function renderTask({ data }) {
  const { relations } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Sześcienną kostkę o objętości <InlineMath math={`${data.cubeVolume}\\text{ dm}^3`} /> włożono do
        prostopadłościennego pudełka, którego krawędzie oznaczono literami <InlineMath math="a" />,{" "}
        <InlineMath math="b" />, <InlineMath math="c" /> (zobacz rysunek).
      </p>
      <p>
        Długość krawędzi sześciennej kostki stanowi <ReadableInlineMath math={fracLatex(relations.a)} scale={1.15} verticalAlign="-0.18em" /> długości
        krawędzi <InlineMath math="a" />, <ReadableInlineMath math={fracLatex(relations.b)} scale={1.15} verticalAlign="-0.18em" /> długości krawędzi{" "}
        <InlineMath math="b" /> i <ReadableInlineMath math={fracLatex(relations.c)} scale={1.15} verticalAlign="-0.18em" /> długości krawędzi{" "}
        <InlineMath math="c" />.
      </p>
      <Diagram data={data} />
      <p><strong>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</strong></p>
      <p>Objętość tego pudełka jest równa</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <ChoiceOptionMath key={index} letter={LETTERS[index]} math={`${formatNumber(option.value)}\\text{ dm}^3`} />
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((_, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${formatNumber(data.volume)}\\text{ dm}^3`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${formatNumber(option.value)}\\text{ dm}^3`} />;
}

export function renderExplanation({ data }) {
  const { relations } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Z objętości kostki wyznaczamy jej krawędź:</p>
      <BlockMath math={`s^3=${data.cubeVolume}\\text{ dm}^3\\quad\\Rightarrow\\quad s=${data.cubeEdge}\\text{ dm}`} />
      <p>
        <strong>Krok 2.</strong> Skoro krawędź kostki jest podaną częścią krawędzi pudełka,
        to długość krawędzi pudełka obliczamy przez dzielenie przez ten ułamek. Dzielenie przez ułamek
        oznacza mnożenie przez jego odwrotność:
      </p>
      <BlockMath math={`a=${data.cubeEdge}: ${fracLatex(relations.a)}=${data.cubeEdge}\\cdot ${reciprocalLatex(relations.a)}=${formatNumber(data.a)}\\text{ dm}`} />
      <BlockMath math={`b=${data.cubeEdge}: ${fracLatex(relations.b)}=${data.cubeEdge}\\cdot ${reciprocalLatex(relations.b)}=${formatNumber(data.b)}\\text{ dm}`} />
      <BlockMath math={`c=${data.cubeEdge}: ${fracLatex(relations.c)}=${data.cubeEdge}\\cdot ${reciprocalLatex(relations.c)}=${formatNumber(data.c)}\\text{ dm}`} />
      <p><strong>Krok 3.</strong> Liczymy objętość pudełka:</p>
      <BlockMath math={`V=a\\cdot b\\cdot c=${formatNumber(data.a)}\\cdot ${formatNumber(data.b)}\\cdot ${formatNumber(data.c)}=${formatNumber(data.volume)}\\text{ dm}^3`} />
      <p>Poprawna odpowiedź to <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.</p>
    </div>
  );
}
